import { MouseEvent, useCallback, useState } from 'react';
import { Box } from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';

import SidebarLink from '@/components/shared/Sidebar/SidebarLink';
import { basicLinkStyles } from '@/components/shared/Sidebar/styles';

import { NotificationPanel } from './NotificationPanel';
import { useNotificationSummary } from './useNotifications';

/** "99+" above 99. */
export const countText = (n: number): string => (n > 99 ? '99+' : String(n));

/**
 * "Notifications" in the sidebar, directly above the role switcher (owner change 1 to the
 * mock-up), built like SidebarRoleMenu: a sidebar link with the bell. The unread count sits on
 * the bell's corner, the same in the 65px sidebar and the wide one, so it never depends on
 * whether the sidebar is open (owner 2026-10-22: a number one time and a dot another, and a
 * badge on top of the bell, were both reported). A click opens the panel beside the sidebar.
 * Nothing at all without a notification backend (useNotificationSummary `absent`) or before
 * its first answer. This entry keeps the summary fresh (polls).
 */
export const SidebarNotifications = () => {
  const { availability, unread } = useNotificationSummary({ poll: true });
  const [anchorTop, setAnchorTop] = useState<number | null>(null);
  const close = useCallback(() => setAnchorTop(null), []);

  if (availability !== 'available') return null;

  const badge =
    unread > 0 ? (
      <Box
        component='span'
        data-testid='sidebar-notifications-count'
        aria-label={`${unread} unread`}
        sx={{
          position: 'absolute',
          top: '-7px',
          left: '13px',
          minWidth: 18,
          height: 18,
          px: '4px',
          boxSizing: 'border-box',
          borderRadius: '9px',
          // a ring in the sidebar's colour keeps the count apart from the bell
          border: '2px solid rgba(15, 23, 42, 0.98)',
          bgcolor: '#e53935',
          color: '#ffffff',
          fontSize: '10px',
          fontWeight: 700,
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        {countText(unread)}
      </Box>
    ) : null;

  return (
    <>
      {/* The click bubbles up from the sidebar link; the wrapper anchors the panel. */}
      <Box
        sx={{ position: 'relative' }}
        data-testid='sidebar-notifications'
        onClick={(e: MouseEvent<HTMLElement>) =>
          setAnchorTop(Math.round(e.currentTarget.getBoundingClientRect().bottom))
        }
      >
        <SidebarLink
          icon={NotificationsNoneOutlinedIcon}
          text='Notifications'
          boxContainerStyles={basicLinkStyles}
          handleClick={() => undefined}
          iconBadge={badge}
        />
      </Box>
      <NotificationPanel anchorTop={anchorTop} onClose={close} />
    </>
  );
};

export default SidebarNotifications;
